<div
    class="host-view"
    cdkScrollable
    [style.background-image]="
        hostViewWallpaperUrl ? 'url(' + hostViewWallpaperUrl + ')' : null
    "
>
    <div class="badge-bar">
        <span class="sync-sweep" aria-hidden="true"></span>
        <div class="badge-bar-header">
            <span class="host-view-label">Host View</span>

            @if (badges.length > 0) {
                <tools-toggle
                    [isChecked]="showBadges"
                    [showCheckedLabel]="true"
                    (change)="onShowBadgesChange($event.checked)"
                >
                    Show Badges
                </tools-toggle>
            }

            @if (tenantLogoUrl) {
                <img class="tenant-logo" [src]="tenantLogoUrl" alt="logo" />
            }
        </div>

        @if (badges.length > 0) {
            <div
                class="badge-panel"
                appContentHeight
                [class.open]="showBadges"
                [class.badge-dragging]="draggingBadgeId !== null"
            >
                <div class="badge-panel-inner">
                    <div class="badge-row">
                        @for (badge of badges; track badge.id; let i = $index) {
                            <div class="badge-slot">
                                @if (draggingBadgeId === badge.id) {
                                    <img
                                        class="badge-image badge-ghost"
                                        [src]="badge.imageUrl"
                                        [alt]="badge.name"
                                    />
                                }
                                <img
                                    class="badge-image"
                                    cdkDrag
                                    [cdkDragData]="badge"
                                    (cdkDragStarted)="
                                        onBadgeDragStarted(badge.id)
                                    "
                                    (cdkDragMoved)="onBadgeDragMoved($event)"
                                    (cdkDragEnded)="
                                        onBadgeDragEnded($event, badge)
                                    "
                                    [appLazyImageSrc]="badge.imageUrl"
                                    [lazyImageDelay]="i * badgeDelayMs"
                                    [alt]="badge.name"
                                />
                            </div>
                        }
                    </div>
                </div>
            </div>
        }
    </div>

    <ol
        class="submissions-list"
        cdkDropList
        (cdkDropListDropped)="onDrop($event)"
    >
        @for (submission of submissions; track submission.id; let i = $index) {
            <li
                class="submission-item"
                [class.is-highlighted]="submission.isHighlighted"
                [class.badge-drop-active]="hoveredBadgeZoneId === submission.id"
                [attr.data-submission-id]="submission.id"
                [attr.aria-current]="submission.isHighlighted ? 'true' : null"
                [attr.tabindex]="0"
                cdkDrag
                (cdkDragStarted)="onListDragStarted()"
                (cdkDragEnded)="onListDragEnded()"
                (click)="onRowClick(submission, $event)"
                (keydown.enter)="onRowClick(submission, $event)"
                (keydown.space)="onRowClick(submission, $event)"
            >
                @if (badges.length > 0) {
                    <div class="badge-zone" cdkDragHandle>
                        <div class="badge">
                            @if (
                                getAssignedBadge(submission);
                                as assignedBadge
                            ) {
                                <img
                                    class="badge-assigned"
                                    draggable="false"
                                    [class.badge-pop]="
                                        animatingBadges.has(submission.id)
                                    "
                                    [src]="assignedBadge.imageUrl"
                                    [alt]="assignedBadge.name"
                                />
                            } @else {
                                <span>Drag and drop badge here</span>
                            }
                        </div>
                    </div>
                }

                <div class="drag-handle" cdkDragHandle>
                    <span class="icon-select_drag"></span>
                </div>

                <!--
                    Tag chips, rendered twice with a different `max`: 4 on their own row at
                    desktop, 2 inline with the play control at `medium`. Two lists rather
                    than CSS-hiding chips, because the `+N` overflow badge comes from the
                    splitter and would otherwise report the wrong count.
                -->
                <ng-template #tagList let-max="max">
                    @if (getTags(submission) | splitter: max; as tagSplit) {
                        @for (
                            tag of tagSplit.visible;
                            track $index + "-" + tag
                        ) {
                            <app-tag
                                class="submission-tag"
                                [option]="{ value: tag, label: tag }"
                            ></app-tag>
                        }
                        @if (tagSplit.hiddenCount > 0) {
                            <app-tag
                                [option]="{
                                    value: null,
                                    label: '+' + tagSplit.hiddenCount
                                }"
                            ></app-tag>
                        }
                    }
                </ng-template>

                <!--
                    Submitter name + location. Declared once and rendered into two slots —
                    the card column at desktop, the content row's right half at `medium` —
                    because the two live under different parents and CSS can't move a node
                    between them. Exactly one is displayed, so the other is out of the a11y
                    tree too.
                -->
                <ng-template #submitterRows>
                    <span class="submitter-item">
                        <span
                            class="icon-iec-profile"
                            aria-hidden="true"
                        ></span>
                        <span>{{ submitterName(submission) }}</span>
                    </span>
                    <span class="submitter-item">
                        <span class="icon-location" aria-hidden="true"></span>
                        <span>{{ submitterLocation(submission) }}</span>
                    </span>
                </ng-template>

                <div class="submission" #summary>
                    <p class="headline">
                        {{ submission.shortDescription }}
                    </p>

                    <div class="submission-content">
                        <div class="submission-items">
                            <!--
                                Two lists rather than CSS-hiding chips: the `+N` badge is
                                computed by the splitter, so hiding chips visually would
                                leave the overflow count wrong.
                            -->
                            <div class="tags tags-large">
                                <ng-container
                                    [ngTemplateOutlet]="tagList"
                                    [ngTemplateOutletContext]="{
                                        max: maxVisibleSubmissionTags
                                    }"
                                ></ng-container>
                            </div>
                            <div class="submission-row">
                                @if (submission.hasAudio) {
                                    <div class="play-row">
                                        <tools-play-icon
                                            class="play-icon"
                                            [track]="
                                                audioTracks.get(submission.id)!
                                            "
                                            [isPlaying]="
                                                isPlaying() &&
                                                nowPlayingId === submission.id
                                            "
                                            [nowPlayingId]="nowPlayingId"
                                            [playlistId]="
                                                navbarService.interactiveId()
                                            "
                                        ></tools-play-icon>
                                        @if (
                                            audioTracks.get(submission.id)
                                                ?.duration
                                        ) {
                                            <span class="audio-duration">
                                                {{
                                                    formatDuration(
                                                        audioTracks.get(
                                                            submission.id
                                                        )!.duration
                                                    )
                                                }}
                                            </span>
                                        }
                                    </div>
                                    <div>
                                        @if (submission.audioEnergy !== null) {
                                            <span class="score-label">
                                                Intensity:
                                                {{ submission.audioEnergy }}%
                                            </span>
                                            <mat-progress-bar
                                                mode="determinate"
                                                [value]="submission.audioEnergy"
                                                class="intensity"
                                            />
                                        }
                                    </div>
                                } @else {
                                    <!--
                                        Not a button: a click here is a row click, which highlights
                                        the row and opens the full text over this spot.
                                    -->
                                    <div
                                        class="text-submission"
                                        cdkOverlayOrigin
                                        #textSubmissionOrigin="cdkOverlayOrigin"
                                    >
                                        <span
                                            class="icon-iec-text-msg"
                                            aria-hidden="true"
                                        ></span>
                                        <span class="text-submission-label">
                                            Text submission
                                        </span>
                                    </div>
                                    @if (fullText(submission); as text) {
                                        <ng-template
                                            cdk-connected-overlay
                                            [cdkConnectedOverlayOrigin]="
                                                textSubmissionOrigin
                                            "
                                            [cdkConnectedOverlayOpen]="
                                                openSubmissionOverlayId ===
                                                submission.id
                                            "
                                            [cdkConnectedOverlayPositions]="
                                                submissionOverlayPositions
                                            "
                                            [cdkConnectedOverlayWidth]="
                                                summary.offsetWidth
                                            "
                                            [cdkConnectedOverlayPush]="true"
                                            [cdkConnectedOverlayHasBackdrop]="
                                                false
                                            "
                                            (overlayOutsideClick)="
                                                closeSubmissionOverlay()
                                            "
                                        >
                                            <div class="submission-overlay">
                                                <div
                                                    class="submission-overlay-header"
                                                >
                                                    <span
                                                        class="submission-overlay-title"
                                                    >
                                                        Full Submission
                                                    </span>
                                                    <button
                                                        class="submission-overlay-close"
                                                        aria-label="Close"
                                                        (click)="
                                                            closeSubmissionOverlay()
                                                        "
                                                    >
                                                        <span
                                                            class="icon-close"
                                                        ></span>
                                                    </button>
                                                </div>
                                                <p
                                                    class="submission-overlay-body"
                                                >
                                                    {{ text }}
                                                </p>
                                            </div>
                                        </ng-template>
                                    }
                                }

                                <!--
                                    Medium shows the chips inline with the play control;
                                    desktop uses `.tags-large` on its own row above.
                                -->
                                <div class="tags tags-medium">
                                    <ng-container
                                        [ngTemplateOutlet]="tagList"
                                        [ngTemplateOutletContext]="{
                                            max: maxVisibleSubmissionTagsMedium
                                        }"
                                    ></ng-container>
                                </div>
                            </div>
                        </div>

                        <!--
                            Tablet slot: takes the right half the notes vacate. Hidden at
                            desktop, where the same rows render in the card column below.
                        -->
                        <div class="submitter-inline">
                            <ng-container
                                [ngTemplateOutlet]="submitterRows"
                            ></ng-container>
                        </div>

                        @if (submission.notes) {
                            <div class="notes">
                                <button
                                    class="notes-toggle"
                                    (click)="toggleNotes(submission.id)"
                                >
                                    Producer's Notes
                                    <span
                                        class="notes-chevron icon-up"
                                        [class.open]="
                                            expandedNotes.has(submission.id)
                                        "
                                    ></span>
                                </button>
                                <div
                                    class="notes-body"
                                    appContentHeight
                                    [class.open]="
                                        expandedNotes.has(submission.id)
                                    "
                                >
                                    <p class="notes-text">
                                        {{ submission.notes }}
                                    </p>
                                </div>
                            </div>
                        }
                    </div>
                </div>

                <!-- Desktop slot; `medium` uses `.submitter-inline` inside the content row. -->
                <div class="submitter">
                    <ng-container
                        [ngTemplateOutlet]="submitterRows"
                    ></ng-container>
                </div>

                <div class="actions">
                    @if (i > 0) {
                        <button
                            class="action-button"
                            aria-label="Move to Top"
                            (click)="moveToTop(submission, i)"
                        >
                            <span class="icon-anchor-top"></span>
                            <span class="action-label">Move to Top</span>
                        </button>
                    }
                    <button
                        class="action-button"
                        aria-label="Remove"
                        (click)="onRemove(submission)"
                    >
                        <span class="icon-trash-outline"></span>
                        <span class="action-label">Remove</span>
                    </button>

                    <!--
                        Desktop keeps the labelled toggle; `medium` swaps to the icon-only
                        check below. Both are in the DOM with one hidden per breakpoint, so
                        the hidden one is out of the a11y tree too.
                    -->
                    <tools-toggle
                        class="mark-used-toggle"
                        [isChecked]="submission.isUsed"
                        (change)="onMarkUsed(submission, $event.checked)"
                    >
                        Mark used
                    </tools-toggle>
                    <button
                        class="mark-used-check"
                        [class.used]="submission.isUsed"
                        [attr.aria-pressed]="submission.isUsed"
                        aria-label="Mark used"
                        (click)="onMarkUsed(submission, !submission.isUsed)"
                    >
                        <span class="icon-check" aria-hidden="true"></span>
                    </button>
                </div>
            </li>
        }
    </ol>
</div>
